import { useCallback, useContext, useMemo, useState } from 'react';

import { debounce } from 'lodash';

import { MainContext } from '@ezbobdev/ui-framework';

export const useAddressAutocomplete = () => {
  const [cityResults, setCityResults] = useState([]);
  const [streetResults, setStreetResults] = useState([]);
  const { actions, sdkActions } = useContext(MainContext);

  const [streetInputValue, setStreetInputValue] = useState('');

  const cityValue = actions.case.getCaseValue('applicant.address.town');
  const addressValue = actions.case.getCaseValue('applicant.address.line1');

  const searchCity = useCallback(
    async (e) => {
      if (e.target.value === '') {
        setCityResults([]);
        return;
      }

      let cityValues = [];

      try {
        const citiesResponse = await sdkActions.address.getCities(e.target.value.trim());
        const cities = citiesResponse?.data || [];
        if (Array.isArray(cities)) {
          cityValues = cities.map((city) => {
            return { value: city };
          });
        }
      } catch (e) {
        console.error(e);
      }

      setCityResults(cityValues);
    },
    [sdkActions.address],
  );

  const searchStreet = useCallback(
    async (e) => {
      if (e.target.value === '') {
        setStreetResults([]);
        return;
      }

      if (!cityValue) {
        setStreetResults([]);
        return;
      }

      let streetValues = [];

      try {
        const streetsResponse = await sdkActions.address.getStreets(cityValue, e.target.value.trim());
        const streets = streetsResponse?.data || [];
        if (Array.isArray(streets)) {
          streetValues = streets.map((street) => {
            return { value: street };
          });
        }
      } catch (e) {
        console.error(e);
      }

      setStreetResults(streetValues);
    },
    [cityValue, sdkActions.address],
  );

  const handleCityChange = useMemo(() => debounce(searchCity, 1000, { leading: false, trailing: true }), [searchCity]);

  const handleStreetChange = useMemo(
    () => debounce(searchStreet, 1000, { leading: false, trailing: true }),
    [searchStreet],
  );

  const handleCityValueChange = useCallback(() => {
    setStreetResults([]);
    setStreetInputValue('');
    actions.case.resetInCase('applicant.address.line1');
  }, [actions.case]);

  const handleCityInputChange = useCallback(
    (e, _, reason) => {
      if (reason === 'input') {
        handleCityChange(e);
      }
      if (reason === 'clear') {
        setCityResults([]);
      }
    },
    [handleCityChange],
  );

  const handleStreetInputChange = useCallback(
    (e, value, reason) => {
      setStreetInputValue(value);

      if (reason === 'input') {
        handleStreetChange(e);
      }
      if (reason === 'clear') {
        setStreetResults([]);
      }
    },
    [handleStreetChange],
  );

  return {
    cityResults,
    streetResults,
    cityValue,
    addressValue,
    streetInputValue,
    handleCityValueChange,
    handleCityInputChange,
    handleStreetInputChange,
  };
};
